import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Loader2, Pencil, Trash2, Users, Phone, Mail, BarChart3 } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { useShowProfits } from '@/hooks/useShowProfits';

const PollenDotsTeam = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const caps = usePollenCapabilities();
  const [showProfits] = useShowProfits();
  const profitVisible = caps.is_super_admin && showProfits;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', phone: '', email: '', dob: '', is_active: true, alerts_recipient: false });
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/team`);
      if (res.ok) setMembers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setForm({ name: '', role: '', phone: '', email: '', dob: '', is_active: true, alerts_recipient: false }); setEditMode(false); setDialogOpen(true); };
  const handleEdit = (m) => { setForm({ name: m.name, role: m.role || '', phone: m.phone || '', email: m.email || '', dob: m.dob || '', is_active: m.is_active !== false, alerts_recipient: !!m.alerts_recipient }); setEditId(m.id); setEditMode(true); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editMode ? `${POLLEN_API}/team/${editId}` : `${POLLEN_API}/team`;
      const res = await authFetch(url, { method: editMode ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editMode ? 'Team member updated' : 'Team member added'); setDialogOpen(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    await authFetch(`${POLLEN_API}/team/${id}`, { method: 'DELETE' });
    toast.success('Team member removed');
    setDeleteId(null);
    fetchData();
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="space-y-4" data-testid="team-page">
        <PollenDotsNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="w-6 h-6" />Team Members</h1>
            <p className="text-muted-foreground text-sm">Track team referrals and the business they generate</p>
          </div>
          <Button onClick={openAdd} data-testid="add-team-member-btn"><Plus className="w-4 h-4 mr-1" />Add Member</Button>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> :
          members.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No team members yet. Click "Add Member" to get started.</CardContent></Card>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {members.map(m => (
                  <Card key={m.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/pollen-dots/team/${m.id}`)}>
                    <CardContent className="p-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{m.name}</p>
                          {m.role && <p className="text-xs text-muted-foreground">{m.role}</p>}
                        </div>
                        {m.is_active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                          : <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {m.phone && <span><Phone className="w-3 h-3 inline mr-1" />{m.phone}</span>}
                        {m.email && <span><Mail className="w-3 h-3 inline mr-1" />{m.email}</span>}
                      </div>
                      <div className={`grid ${profitVisible ? 'grid-cols-4' : 'grid-cols-3'} gap-2 pt-2 border-t text-center`}>
                        <StatMini label="Referred" value={m.referred_customers_count || 0} />
                        <StatMini label="Tours" value={m.total_tours || 0} />
                        <StatMini label="Revenue" value={`${((m.total_revenue || 0) / 100000).toFixed(1)}L`} />
                        {profitVisible && m.total_profit != null && <StatMini label="Profit" value={`${((m.total_profit || 0) / 100000).toFixed(1)}L`} />}
                      </div>
                      <div className="flex justify-end gap-1 pt-1">
                        {caps.is_super_admin && m.email && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]"
                            onClick={e => { e.stopPropagation(); navigate(`/pollen-dots/profile/by-email/${encodeURIComponent(m.email || '')}`); }}
                            data-testid={`view-perf-mobile-${m.id}`}>
                            <BarChart3 className="w-3 h-3 mr-1" />Performance
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); handleEdit(m); }}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={e => { e.stopPropagation(); setDeleteId(m.id); }}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Referred Customers</TableHead>
                    <TableHead className="text-right">Tours</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    {profitVisible && <TableHead className="text-right">Gross Profit</TableHead>}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {members.map(m => (
                      <TableRow key={m.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/pollen-dots/team/${m.id}`)} data-testid={`team-row-${m.id}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.role || '-'}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {m.phone && <div>{m.phone}</div>}
                          {m.email && <div>{m.email}</div>}
                          {!m.phone && !m.email && '-'}
                        </TableCell>
                        <TableCell>
                          {m.is_active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                            : <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{m.referred_customers_count || 0}</TableCell>
                        <TableCell className="text-right">{m.total_tours || 0}</TableCell>
                        <TableCell className="text-right font-medium">{(m.total_revenue || 0).toLocaleString('en-IN')}</TableCell>
                        {profitVisible && <TableCell className={`text-right font-medium ${(m.total_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{m.total_profit != null ? (m.total_profit || 0).toLocaleString('en-IN') : '—'}</TableCell>}
                        <TableCell className="text-right">
                          {caps.is_super_admin && m.email && (
                            <Button variant="ghost" size="sm" className="text-xs mr-1"
                              onClick={e => { e.stopPropagation(); navigate(`/pollen-dots/profile/by-email/${encodeURIComponent(m.email || '')}`); }}
                              data-testid={`view-perf-${m.id}`}>
                              <BarChart3 className="w-4 h-4 mr-1" />Performance
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleEdit(m); }}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={e => { e.stopPropagation(); setDeleteId(m.id); }}><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Add'} Team Member</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="input-team-name" /></div>
              <div><Label>Role / Title</Label><Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g., Sales Lead, Operations" /></div>
              <div><Label>Phone <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 9317752915, +91 9805541209" /></div>
              <div><Label>Email <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input type="text" inputMode="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Date of birth <span className="text-[10px] text-muted-foreground">(YYYY-MM-DD — surfaces a Pollen Dots wish on their day)</span></Label>
                <Input type="date" value={form.dob || ''}
                       onChange={e => setForm({ ...form, dob: e.target.value })}
                       data-testid="input-team-dob" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active
              </label>
              <label className="flex items-center gap-2 text-sm" title="When checked, this member's phone is included in the daily WhatsApp alerts broadcast">
                <input
                  type="checkbox"
                  checked={form.alerts_recipient}
                  onChange={e => setForm({ ...form, alerts_recipient: e.target.checked })}
                  data-testid="input-alerts-recipient"
                /> Receives daily alerts broadcast (WhatsApp)
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-team-member">{submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editMode ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <ConfirmDeleteDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => handleDelete(deleteId)} title="Remove Team Member" description="This will remove the team member. Customers they referred will keep the referrer reference but the name will show as unknown." />
      </div>
    </Layout>
  );
};

const StatMini = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
    <p className="text-sm font-bold">{value}</p>
  </div>
);

export default PollenDotsTeam;
